import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  content:
    "Bonjour et bienvenue chez DIMORA ! Je suis l'Assistant DIMORA, disponible 24h/24. Une question sur nos services, nos tarifs ou notre zone d'intervention (Gap, 04 & 05) ?",
};

const SUGGESTIONS = ["Quels sont vos tarifs ?", "Vous intervenez où ?", "Je veux un devis"];

export const ChatWidget = ({ onPrefill }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [filling, setFilling] = useState(false);
  const sessionId = useRef(crypto.randomUUID());
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const appendDelta = (delta) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
      return copy;
    });

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: content }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) appendDelta(parsed.delta);
            if (parsed.error) throw new Error(parsed.error);
          } catch (e) {
            if (e.message === "Assistant indisponible") throw e;
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Petit souci technique de mon côté… Appelez-nous au 06 18 90 74 35, on vous répond tout de suite !",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const prefill = async () => {
    if (filling) return;
    setFilling(true);
    try {
      const res = await fetch(`${API}/chat/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current }),
      });
      if (!res.ok) throw new Error("quote failed");
      const data = await res.json();
      onPrefill(data);
      setOpen(false);
      toast.success("Devis pré-rempli ! Vérifiez et complétez vos coordonnées.");
      const target = document.querySelector("#devis");
      if (window.__lenis && target) window.__lenis.scrollTo(target, { offset: -70, duration: 1.4 });
      else target?.scrollIntoView({ behavior: "smooth" });
    } catch {
      toast.error("Impossible de pré-remplir. Échangez un peu plus avec l'assistant.");
    } finally {
      setFilling(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-open-button"
        onClick={() => setOpen(!open)}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.8, type: "spring", stiffness: 200 }}
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-amber-400 text-[#0A0D14] flex items-center justify-center shadow-[0_0_30px_rgba(242,193,78,0.4)] transition-all duration-300 hover:scale-110 active:scale-95"
        aria-label="Ouvrir l'assistant DIMORA"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-5 z-[60] w-[calc(100vw-2.5rem)] max-w-sm h-[70vh] max-h-[580px] rounded-3xl border border-white/10 bg-[#0D1220]/95 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-gradient-to-r from-amber-400/10 to-transparent">
              <LogoMark className="w-9 h-9" />
              <div className="flex-1">
                <p className="font-display font-bold text-white text-sm tracking-wide">Assistant DIMORA</p>
                <p className="text-[10px] text-emerald-300 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> En ligne — réponse immédiate
                </p>
              </div>
              <button
                data-testid="chat-close-button"
                onClick={() => setOpen(false)}
                className="text-slate-400 hover:text-white transition-colors p-1"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} data-testid={`chat-message-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-amber-400 text-[#0A0D14] font-medium rounded-br-md"
                        : "bg-white/[0.07] text-slate-200 border border-white/10 rounded-bl-md"
                    }`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={s}
                      data-testid={`chat-suggestion-${i}`}
                      onClick={() => send(s)}
                      className="text-xs rounded-full border border-amber-400/30 text-amber-200 px-3 py-1.5 hover:bg-amber-400/10 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {messages.length > 1 && (
              <div className="px-4 pb-2">
                <button
                  data-testid="chat-prefill-button"
                  onClick={prefill}
                  disabled={filling}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-400/10 text-emerald-200 text-xs font-semibold py-2.5 transition-all duration-300 hover:bg-emerald-400/20 disabled:opacity-60"
                >
                  {filling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {filling ? "Analyse de la conversation..." : "Pré-remplir mon devis avec l'IA"}
                </button>
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 px-4 py-3 border-t border-white/10"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Écrivez votre message..."
                className="flex-1 rounded-full bg-white/5 border border-white/10 px-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-400/60 transition-colors"
              />
              <button
                type="submit"
                data-testid="chat-send-button"
                disabled={streaming || !input.trim()}
                className="w-10 h-10 rounded-full bg-amber-400 text-[#0A0D14] flex items-center justify-center transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-50"
                aria-label="Envoyer"
              >
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
            <p className="text-center text-[9px] text-slate-600 pb-2.5">Assistant propulsé par ChatGPT — réponses indicatives</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
